import "./payment-sums-table.css";
import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  useMonthlyPaymentSums,
  MonthlyPaymentSumRow,
} from "./hooks/useMonthlyPaymentSums";
import CollapsibleSection from "../../../../components/CollapsibleSection";
import { projectToFullMonth, projectedChangePercent } from "../../../../utils/monthExtrapolation";

interface MonthlyPaymentSumsSectionProps {
  toastRef: React.RefObject<Toast>;
  /** Reference date for the current-month projection; injectable for tests. */
  now?: Date;
}

const euro = (value: number) =>
  (value ?? 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const totalOf = (row: MonthlyPaymentSumRow) =>
  row.eroundSum != null ? row.directPaySum + row.appPaySum + row.eroundSum : null;

// Current-month run-rate projection: amount so far / completed days (today's
// data isn't in yet) * days in month, compared against the previous month.
// Hidden on the 1st, when no day is complete.
const Projection = ({
  current,
  previous,
  now,
}: {
  current: number | null;
  previous: number | null | undefined;
  now: Date;
}) => {
  if (current == null) return null;
  const projected = projectToFullMonth(current, now);
  if (projected == null) return null;
  const change = projectedChangePercent(current, previous, now);
  const isPositive = (change ?? 0) >= 0;

  return (
    <div
      className="text-color-secondary text-sm font-normal mt-1"
      title={`Extrapolated to the full month from ${now.getDate() - 1} completed day(s), vs last month`}
    >
      ≈ {euro(projected)}
      {change != null && (
        <span className="ml-1 font-semibold" style={{ color: isPositive ? "#16a34a" : "#ef4444" }}>
          {isPositive ? "↑" : "↓"} {Math.abs(change).toFixed(1).replace(".", ",")} %
        </span>
      )}
    </div>
  );
};

const monthLabel = (row: MonthlyPaymentSumRow) =>
  new Date(row.year, row.month - 1).toLocaleString("de-DE", {
    month: "long",
    year: "numeric",
  });

const lastUpdatedLabel = (row: MonthlyPaymentSumRow) =>
  row.lastUpdated ? new Date(row.lastUpdated).toLocaleString("de-DE") : "—";

const eroundUpdatedLabel = (row: MonthlyPaymentSumRow) =>
  row.eroundLastUpdated ? new Date(row.eroundLastUpdated).toLocaleString("de-DE") : "—";

// Total = Direct Pay + App Pay + eRound. The backend `total` field only sums
// direct + app, so we compute the displayed total here to fold eRound in. When
// the eRound sum isn't available yet the total is partial (direct + app only);
// mark it with a "*" + tooltip so a partial figure isn't read as final. It fills
// in on the next refresh once eRound arrives.
const totalLabel = (row: MonthlyPaymentSumRow) => {
  const base = row.directPaySum + row.appPaySum;
  if (row.eroundSum != null) {
    return euro(base + row.eroundSum);
  }
  return (
    <span
      className="text-color-secondary"
      title="eRound not yet included — refresh once available"
    >
      {euro(base)}*
    </span>
  );
};

// eRound sum is best-effort on the backend. A present value (status READY, or a
// legacy row without a status) renders the formatted EUR amount; otherwise the
// nullable eroundStatus tells us why it's missing so we can guide the user.
const eroundLabel = (row: MonthlyPaymentSumRow) => {
  if (row.eroundSum != null) {
    return euro(row.eroundSum);
  }
  if (row.eroundStatus === "GENERATING") {
    return (
      <span
        className="text-color-secondary font-italic"
        title="eRound report is still being computed"
      >
        generating…
      </span>
    );
  }
  if (row.eroundStatus === "FAILED" || row.eroundStatus === "UNAVAILABLE") {
    return (
      <span className="text-orange-500" title="eRound fetch failed — try refresh">
        unavailable
      </span>
    );
  }
  // legacy row: no status and no sum yet (refresh to retry).
  return <span className="text-color-secondary font-italic">pending</span>;
};

export default function MonthlyPaymentSumsSection({
  toastRef,
  now = new Date(),
}: MonthlyPaymentSumsSectionProps) {
  const {
    rows,
    loading,
    error,
    refreshing,
    rowRefreshing,
    refreshRecent,
    refreshMonth,
  } = useMonthlyPaymentSums();

  const onRefreshRecent = async () => {
    const result = await refreshRecent();
    if (!result) {
      toastRef.current?.show({
        severity: "error",
        summary: "Refresh failed",
        detail: "Could not refresh the recent months.",
        life: 6000,
      });
      return;
    }
    if (result.failedMonths.length > 0) {
      const months = result.failedMonths.map((m) => `${m.month}/${m.year}`).join(", ");
      toastRef.current?.show({
        severity: "warn",
        summary: "Some months could not be refreshed",
        detail: `Kept previous values for: ${months}`,
        life: 6000,
      });
    }
  };

  const onRefreshMonth = async (year: number, month: number) => {
    const ok = await refreshMonth(year, month);
    if (!ok) {
      toastRef.current?.show({
        severity: "error",
        summary: "Refresh failed",
        detail: `Could not refresh ${month}/${year}.`,
        life: 6000,
      });
    }
  };

  const refreshAction = (
    <Button
      label="Refresh recent"
      icon="pi pi-refresh"
      severity="secondary"
      outlined
      loading={refreshing}
      onClick={onRefreshRecent}
      tooltip="Recomputes current + 3 prior months"
      tooltipOptions={{ position: "left" }}
    />
  );

  const isCurrentMonth = (row: MonthlyPaymentSumRow) =>
    row.year === now.getFullYear() && row.month === now.getMonth() + 1;

  const previousMonthOf = (row: MonthlyPaymentSumRow) => {
    const prevYear = row.month === 1 ? row.year - 1 : row.year;
    const prevMonth = row.month === 1 ? 12 : row.month - 1;
    return rows.find((r) => r.year === prevYear && r.month === prevMonth);
  };

  // Wraps a cell renderer so the current-month row also shows its projection.
  const withProjection =
    (
      render: (row: MonthlyPaymentSumRow) => React.ReactNode,
      amountOf: (row: MonthlyPaymentSumRow) => number | null,
    ) =>
    (row: MonthlyPaymentSumRow) => {
      if (!isCurrentMonth(row)) return render(row);
      const previous = previousMonthOf(row);
      return (
        <>
          {render(row)}
          <Projection
            current={amountOf(row)}
            previous={previous ? amountOf(previous) : null}
            now={now}
          />
        </>
      );
    };

  const refreshBody = (row: MonthlyPaymentSumRow) => (
    <Button
      icon="pi pi-refresh"
      rounded
      text
      aria-label={`Refresh ${row.month}/${row.year}`}
      loading={!!rowRefreshing[row.id]}
      onClick={() => onRefreshMonth(row.year, row.month)}
    />
  );

  return (
    <CollapsibleSection
      title="Monthly Payment Sums"
      storageKey="clearing.monthlyPaymentSums"
      actions={loading || error ? null : refreshAction}
    >
      {loading && (
        <div className="flex justify-content-center my-5">
          <ProgressSpinner />
        </div>
      )}

      {!loading && error && (
        <p className="text-red-500 m-0">Failed to load monthly payment sums.</p>
      )}

      {!loading && !error && (
        <DataTable
          value={rows}
          dataKey="id"
          emptyMessage="No data"
          className="payment-sums-table"
          data-testid="payment-sums-table"
          stripedRows
        >
          <Column body={refreshBody} style={{ width: "4rem" }} />
          <Column header="Month" body={monthLabel} />
          <Column
            header="Direct Pay"
            body={withProjection((r) => euro(r.directPaySum), (r) => r.directPaySum)}
          />
          <Column
            header="App Pay"
            body={withProjection((r) => euro(r.appPaySum), (r) => r.appPaySum)}
          />
          <Column header="eRound" body={withProjection(eroundLabel, (r) => r.eroundSum)} />
          <Column header="eRound updated" body={eroundUpdatedLabel} />
          <Column header="Sums updated" body={lastUpdatedLabel} />
          <Column header="Total" body={withProjection(totalLabel, totalOf)} className="font-bold" />
        </DataTable>
      )}
    </CollapsibleSection>
  );
}
